import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener('change', this.#cruxHandleVariantOptionChange, { signal });

    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const cruxShouldKeepMlpGallery =
      event.detail.data.html.querySelector('variant-picker[data-product-url*="mlp-pinkie-pie-series-ear-accessories-set"]') ||
      this.closest('.shopify-section, featured-product-information, dialog')?.querySelector(
        'variant-picker[data-product-url*="mlp-pinkie-pie-series-ear-accessories-set"]'
      );

    if (cruxShouldKeepMlpGallery) {
      this.#cruxSyncMlpGalleryToVariantMedia(event, newMediaGallery);
      return;
    }

    if (this.#cruxSyncVariantMediaPreview(event, newMediaGallery)) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Immediately selects the variant image that is already present in the
   * product gallery before the section request finishes.
   * @param {Event} event - The option change event.
   */
  #cruxHandleVariantOptionChange = (event) => {
    const changed = event.target;
    if (!(changed instanceof HTMLElement)) return;
    const picker = changed.closest('variant-picker');
    if (!picker) return;

    const values = Array.from(picker.querySelectorAll('fieldset, select'))
      .map((group) => {
        if (group instanceof HTMLSelectElement) return group.options[group.selectedIndex]?.textContent?.trim() || group.value;
        return group.querySelector('input:checked')?.value || '';
      })
      .filter(Boolean);
    const directVariantId = changed.dataset.variantId;
    const variants = Array.from(document.querySelectorAll('script[type="application/ld+json"]')).flatMap((script) => {
      try {
        const data = JSON.parse(script.textContent || '{}');
        return Array.isArray(data?.hasVariant) ? data.hasVariant : [];
      } catch {
        return [];
      }
    });
    const suffix = values.join(' / ');
    const variant =
      variants.find((item) => directVariantId && item?.['@id']?.includes('variant=' + directVariantId)) ||
      variants.find((item) => suffix && String(item?.name || '').endsWith(' - ' + suffix)) ||
      variants.find((item) => values[0] && String(item?.name || '').includes(' - ' + values[0] + ' / '));
    const imageUrl = typeof variant?.image === 'string' ? variant.image : variant?.image?.url;
    if (imageUrl) this.#cruxSelectVariantImage(imageUrl);
  };

  #cruxSelectVariantImage(imageUrl) {
    const mediaKey = (value) => value?.toString?.().split('?')[0].split('/').pop();
    const imageKey = mediaKey(imageUrl);
    if (!imageKey) return false;
    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const targetIndex = slides.findIndex((slide) => mediaKey(slide.querySelector('img')?.currentSrc || slide.querySelector('img')?.src) === imageKey);
    if (targetIndex < 0) return false;
    return this.#cruxApplyVariantMediaIndex(targetIndex);
  }

  #cruxApplyVariantMediaIndex(targetIndex) {
    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const targetSlide = slides[targetIndex];
    const slideshow = this.slideshow;
    const scroller = this.querySelector('slideshow-slides');
    if (!targetSlide || !slideshow || !scroller) return false;
    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', String(index !== targetIndex));
      slide.removeAttribute('reveal');
    });
    Array.from(this.querySelectorAll('slideshow-controls [aria-selected]'))
      .forEach((thumbnail, index) => thumbnail.setAttribute('aria-selected', String(index === targetIndex)));
    scroller.style.scrollBehavior = 'auto';
    scroller.style.scrollSnapType = 'none';
    scroller.scrollLeft = targetSlide.offsetLeft;
    slideshow.current = targetIndex;
    return true;
  }

  /**
   * Same-product media switch without slideshow reordering.
   * @param {VariantUpdateEvent} event - The variant update event.
   * @param {HTMLElement} newMediaGallery - The freshly fetched media gallery.
   * @returns {boolean} Whether the current gallery handled the update.
   */
  #cruxSyncVariantMediaPreview(event, newMediaGallery) {
    const currentPicker = this.closest('.shopify-section, featured-product-information, dialog')?.querySelector(
      'variant-picker[data-product-url]'
    );
    const nextPicker =
      newMediaGallery.closest('.shopify-section, featured-product-information, dialog')?.querySelector(
        'variant-picker[data-product-url]'
      ) || event.detail.data.html.querySelector('variant-picker[data-product-url]');
    const currentProductUrl = currentPicker?.dataset.productUrl?.split('?')[0];
    const nextProductUrl = nextPicker?.dataset.productUrl?.split('?')[0];
    if (currentProductUrl && nextProductUrl && currentProductUrl !== nextProductUrl) return false;

    const mediaKey = (value) => value?.toString?.().split('?')[0].split('/').pop();
    const selectedMediaId =
      event.detail.resource?.featured_media?.id?.toString?.() ||
      event.detail.resource?.featured_image?.id?.toString?.() ||
      null;
    const selectedImageKey = mediaKey(
      event.detail.resource?.featured_media?.preview_image?.src ||
        event.detail.resource?.featured_image?.src ||
        event.detail.resource?.image?.src
    );
    if (!selectedMediaId && !selectedImageKey) return true;

    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    let targetIndex = slides.findIndex((slide) =>
      slide.querySelector('.product-media[data-media-id]')?.dataset.mediaId === selectedMediaId
    );
    if (targetIndex < 0 && selectedImageKey) {
      targetIndex = slides.findIndex((slide) => mediaKey(slide.querySelector('img')?.currentSrc || slide.querySelector('img')?.src) === selectedImageKey);
    }
    if (targetIndex < 0) return true;
    return this.#cruxApplyVariantMediaIndex(targetIndex);
  }

  /**
   * Keeps the MLP featured/product gallery stable while still switching to the selected SKU image.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   * @param {HTMLElement} newMediaGallery - The freshly fetched media gallery.
   */
  #cruxSyncMlpGalleryToVariantMedia(event, newMediaGallery) {
    const selectedMediaId =
      event.detail.resource?.featured_media?.id?.toString?.() ||
      event.detail.resource?.featured_image?.id?.toString?.() ||
      newMediaGallery
        .querySelector('slideshow-slide[aria-hidden="false"] .product-media[data-media-id]')
        ?.dataset.mediaId;

    if (!selectedMediaId) return;

    const slides = Array.from(this.querySelectorAll('slideshow-slide'));
    const targetIndex = slides.findIndex((slide) => {
      const media = slide.querySelector('.product-media[data-media-id]');
      return media?.dataset.mediaId === selectedMediaId;
    });

    if (targetIndex < 0) return;

    this.slideshow?.select(targetIndex, undefined, { animate: false });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
